import { useState } from 'react';
import { createRoute } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { authedLayoutRoute } from './authed.js';
import { getLeaveLiabilityReport, downloadLeaveLiability, type LiabilityRow, type LeaveLiabilityReport } from '../api/reports.js';
import { ApiErrorNotice } from '../components/ApiErrorNotice.js';
import { Alert } from '../components/Alert.js';
import { formatMoney } from '../lib/format.js';
import type { CurrencyCode } from '@kora/types';

export const reportsLeaveLiabilityRoute = createRoute({
  getParentRoute: () => authedLayoutRoute,
  path: '/reports/leave-liability',
  component: ReportsLeaveLiabilityPage,
});

const grp = (g: string | null): string => g ?? '(none)';
/** Date-only display — meta.asOf is an ISO datetime (end-of-day). */
const asOfDate = (iso: string): string => iso.slice(0, 10);

function ReportsLeaveLiabilityPage(): JSX.Element {
  // As-of snapshot (D2) — a single date, not a period range.
  const [asOf, setAsOf] = useState('2026-06-30');
  const [dept, setDept] = useState('');
  const filters = { asOf, ...(dept ? { department: dept } : {}) };
  const q = useQuery({
    queryKey: ['reports', 'leave-liability', asOf, dept],
    queryFn: () => getLeaveLiabilityReport(filters),
    enabled: Boolean(asOf),
  });
  const [dlErr, setDlErr] = useState<unknown>(null);
  const download = async (format: 'csv' | 'pdf'): Promise<void> => {
    setDlErr(null);
    try { await downloadLeaveLiability(filters, format); } catch (e) { setDlErr(e); }
  };

  return (
    <section className="p-8 space-y-6 max-w-4xl">
      <header className="space-y-1">
        <h1 className="font-serif text-3xl">
          {q.data ? `${q.data.meta.headline} — as of ${asOfDate(q.data.meta.asOf)}` : 'Leave liability'}
        </h1>
        <p className="text-sm text-gray-400">What you would owe for accrued-unused annual leave if everyone were paid out as of a date. Per currency, never mixed.</p>
      </header>

      <div className="flex flex-wrap items-end gap-3 rounded border border-gray-900 p-4">
        <label className="text-sm text-gray-300 space-y-1"><span>As of</span>
          <input type="date" value={asOf} onChange={(e) => setAsOf(e.target.value)} aria-label="As-of date" className="block rounded border border-gray-700 bg-gray-900 px-3 py-2 text-white text-sm" />
        </label>
        <label className="text-sm text-gray-300 space-y-1"><span>Department <span className="text-gray-500">(optional)</span></span>
          <input value={dept} onChange={(e) => setDept(e.target.value)} aria-label="Department filter" placeholder="All" className="block rounded border border-gray-700 bg-gray-900 px-3 py-2 text-white text-sm" />
        </label>
        <div className="flex gap-2">
          <button type="button" onClick={() => void download('csv')} disabled={!q.data} className="rounded border border-gray-700 px-3 py-2 text-sm hover:bg-gray-900 disabled:opacity-40">CSV (opens in Excel)</button>
          <button type="button" onClick={() => void download('pdf')} disabled={!q.data} className="rounded border border-gray-700 px-3 py-2 text-sm hover:bg-gray-900 disabled:opacity-40">PDF</button>
        </div>
      </div>

      {/* A 402 PLAN_REQUIRED renders the upgrade prompt → /billing (self-serve). */}
      <ApiErrorNotice error={q.error} fallback="Could not load the report." />
      <ApiErrorNotice error={dlErr} fallback="Download failed." />

      {q.isLoading ? <p className="text-sm text-gray-500">Loading…</p> : null}

      {q.data ? <LiabilityBody report={q.data} /> : null}
    </section>
  );
}

function LiabilityBody({ report }: { report: LeaveLiabilityReport }): JSX.Element {
  const noComp = report.meta.noCompensationCount;
  return (
    <div className="space-y-6">
      {/* Basis + coverage MUST render — the number's scope + completeness can't live only in JSON (D7/D12). */}
      <div className="space-y-1 text-xs text-gray-500">
        <p>{report.meta.basisNote}</p>
        {report.meta.filterNote ? <p className="text-amber">{report.meta.filterNote}</p> : null}
        <p>Employees covered: {report.meta.employeeCount}</p>
        {/* The D7 signal — a total that quietly excludes people is the failure this prevents. */}
        {noComp > 0 ? (
          <p className="text-amber">
            {noComp} employee{noComp === 1 ? '' : 's'} had no compensation record on this date and contributed 0 — the total excludes their leave.
          </p>
        ) : null}
      </div>
      {report.byCurrency.length === 0 ? (
        <Alert variant="info" compact>No leave liability as of this date.</Alert>
      ) : (
        report.byCurrency.map((c) => (
          <div key={c.currency} className="rounded border border-gray-900 p-4 space-y-3">
            <div className="flex items-baseline justify-between">
              <h2 className="text-lg text-white">{c.currency}</h2>
              <span className="text-xs text-gray-500">{c.employeeCount} employee{c.employeeCount === 1 ? '' : 's'} · {c.unusedDays} days</span>
            </div>
            <div className="grid grid-cols-2 gap-3 text-sm">
              <Metric label="Liability" value={fmt(c.liability, c.currency)} />
              <Metric label="Unused annual days" value={c.unusedDays} />
            </div>
            <AxisTable title="By department" currency={c.currency} rows={report.byDepartment.filter((r) => r.currency === c.currency)} />
            <AxisTable title="By country" currency={c.currency} rows={report.byCountry.filter((r) => r.currency === c.currency)} />
          </div>
        ))
      )}
    </div>
  );
}
/** Display-only money formatting (thousands + 2dp); API keeps fixed-4 for export. */
function fmt(amount: string, currency: string): string {
  try { return formatMoney(amount, currency as CurrencyCode); } catch { return amount; }
}
function Metric({ label, value }: { label: string; value: string }): JSX.Element {
  return <div><div className="text-xs text-gray-500">{label}</div><div className="text-gray-200 font-mono">{value}</div></div>;
}
function AxisTable({ title, currency, rows }: { title: string; currency: string; rows: LiabilityRow[] }): JSX.Element {
  if (rows.length === 0) return <></>;
  return (
    <div className="space-y-1">
      <h3 className="text-xs uppercase tracking-wide text-gray-500">{title}</h3>
      <table className="w-full text-sm">
        <tbody>
          {rows.map((r) => (
            <tr key={grp(r.group)} className="border-t border-gray-900">
              <td className="py-1.5 pr-4 text-gray-200">{grp(r.group)}</td>
              <td className="py-1.5 pr-4 text-right font-mono text-gray-300">{fmt(r.liability, currency)}</td>
              <td className="py-1.5 pr-4 text-right font-mono text-gray-500">{r.unusedDays} days</td>
              <td className="py-1.5 text-right text-gray-500">{r.employeeCount}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
